"use client"

import React from 'react'
import { CheckCircle, AlertCircle, Clock, HelpCircle } from 'lucide-react'
import { Badge, BadgeProps } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

type VerificationStatus = 'verified' | 'unverified' | 'pending' | 'failed' | 'exempt' | 'not-applicable'

interface TaxVerificationBadgeProps extends Omit<BadgeProps, 'children'> {
  status: VerificationStatus
  label?: string
  showTooltip?: boolean
  tooltipText?: string
  size?: 'sm' | 'md'
}

export function TaxVerificationBadge({
  status,
  label,
  showTooltip = true,
  tooltipText,
  size = 'md',
  className,
  ...props
}: TaxVerificationBadgeProps) {
  // Map status to visual appearance
  const statusConfig: Record<VerificationStatus, {
    icon: React.ReactNode,
    text: string,
    color: string,
    defaultTooltip: string
  }> = {
    'verified': {
      icon: <CheckCircle className={size === 'sm' ? 'h-3 w-3 mr-1' : 'h-3.5 w-3.5 mr-1.5'} />,
      text: 'Verified',
      color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 border-green-200',
      defaultTooltip: 'Tax information has been verified'
    },
    'unverified': {
      icon: <AlertCircle className={size === 'sm' ? 'h-3 w-3 mr-1' : 'h-3.5 w-3.5 mr-1.5'} />,
      text: 'Unverified',
      color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100 border-amber-200',
      defaultTooltip: 'Tax information has not been verified yet'
    },
    'pending': {
      icon: <Clock className={size === 'sm' ? 'h-3 w-3 mr-1' : 'h-3.5 w-3.5 mr-1.5'} />,
      text: 'Verification Pending',
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100 border-blue-200',
      defaultTooltip: 'Tax information verification is in progress'
    },
    'failed': {
      icon: <AlertCircle className={size === 'sm' ? 'h-3 w-3 mr-1' : 'h-3.5 w-3.5 mr-1.5'} />,
      text: 'Verification Failed',
      color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100 border-red-200',
      defaultTooltip: 'Tax information verification failed'
    },
    'exempt': {
      icon: <CheckCircle className={size === 'sm' ? 'h-3 w-3 mr-1' : 'h-3.5 w-3.5 mr-1.5'} />,
      text: 'Tax Exempt',
      color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100 border-purple-200',
      defaultTooltip: 'Entity is exempt from this tax'
    },
    'not-applicable': {
      icon: <HelpCircle className={size === 'sm' ? 'h-3 w-3 mr-1' : 'h-3.5 w-3.5 mr-1.5'} />,
      text: 'Not Applicable',
      color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border-gray-200',
      defaultTooltip: 'Tax is not applicable to this entity'
    }
  }

  const config = statusConfig[status]
  const displayText = label || config.text
  const tooltip = tooltipText || config.defaultTooltip

  const badge = (
    <Badge
      variant="outline"
      className={`flex items-center ${config.color} ${size === 'sm' ? 'text-xs py-0.5 px-1.5' : 'text-xs py-1 px-2'} ${className}`}
      {...props}
    >
      {config.icon}
      {displayText}
    </Badge>
  )

  if (showTooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {badge}
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return badge
} 